import React, { useMemo, useState } from "react";

type Word = { text: string; polish: string; czech?: string };

type Props = {
  words: Word[];
  /** domyślnie 10 — ile par wziąć z listy (aby nie przeciążyć strony) */
  limit?: number;
  /** czy używać tłumaczeń czeskich, jeżeli są */
  useCzechIfAvailable?: boolean;
};

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const MatchWordsAdults: React.FC<Props> = ({
  words,
  limit = 10,
  useCzechIfAvailable = false,
}) => {
  const pairs = useMemo(() => {
    const base = words.slice(0, limit).map((w, i) => ({
      id: i,
      left: w.text.trim(),
      right: (useCzechIfAvailable && w.czech ? w.czech : w.polish).trim(),
    }));
    return base;
  }, [words, limit, useCzechIfAvailable]);

  const [left] = useState(() =>
    shuffle(pairs.map((p) => ({ id: p.id, label: p.left }))),
  );
  const [right, setRight] = useState(() =>
    shuffle(pairs.map((p) => ({ id: p.id, label: p.right }))),
  );

  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [selectedRight, setSelectedRight] = useState<number | null>(null);
  const [matched, setMatched] = useState<Set<number>>(new Set());
  const [mist, setMist] = useState(0);

  const reset = () => {
    setSelectedLeft(null);
    setSelectedRight(null);
    setMatched(new Set());
    setRight(shuffle(right));
    setMist(0);
  };

  const tryMatch = (l: number | null, r: number | null) => {
    if (l === null || r === null) return;
    if (l === r) {
      const next = new Set(matched);
      next.add(l);
      setMatched(next);
      setSelectedLeft(null);
      setSelectedRight(null);
    } else {
      setMist((m) => m + 1);
      // krótkie "wstrząśnięcie" błędnych wyborów
      setTimeout(() => {
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 350);
    }
  };

  return (
    <div className="bg-white shadow-md rounded-xl p-4">
      <div className="flex justify-between items-center mb-3">
        <h4 className="font-bold">Ćwiczenie: Dopasuj słowa</h4>
        <div className="text-sm opacity-70">
          Błędy: {mist} • Trafione: {matched.size}/{pairs.length}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        {/* Left */}
        <div className="min-w-0 space-y-2">
          {left.map((item) => {
            const isMatched = matched.has(item.id);
            const isSelected = selectedLeft === item.id;
            return (
              <button
                type="button"
                key={item.id}
                disabled={isMatched}
                onClick={() => {
                  const v = isSelected ? null : item.id;
                  setSelectedLeft(v);
                  tryMatch(v, selectedRight);
                }}
                className={`min-w-0 w-full whitespace-normal break-words text-left px-3 py-2 rounded-lg border transition
                  ${
                    isMatched
                      ? "bg-green-100 border-green-300 text-green-800"
                      : isSelected
                        ? "bg-zinc-100 border-zinc-300"
                        : "bg-white border-zinc-200 hover:bg-zinc-50"
                  }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        {/* Right */}
        <div className="min-w-0 space-y-2">
          {right.map((item) => {
            const isMatched = matched.has(item.id);
            const isSelected = selectedRight === item.id;
            return (
              <button
                type="button"
                key={item.id}
                disabled={isMatched}
                onClick={() => {
                  const v = isSelected ? null : item.id;
                  setSelectedRight(v);
                  tryMatch(selectedLeft, v);
                }}
                className={`min-w-0 w-full whitespace-normal break-words text-left px-3 py-2 rounded-lg border transition
                  ${
                    isMatched
                      ? "bg-green-100 border-green-300 text-green-800"
                      : isSelected
                        ? "bg-zinc-100 border-zinc-300"
                        : "bg-white border-zinc-200 hover:bg-zinc-50"
                  }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 flex gap-2">
        <button
          onClick={reset}
          className="px-3 py-2 rounded-md text-white font-semibold"
          style={{ backgroundColor: "var(--book-accent, #F6851F)" }}
        >
          Wyczyść wszystkie
        </button>
        {matched.size === pairs.length && (
          <div className="px-3 py-2 rounded-md bg-green-600 text-white font-semibold">
            {" "}
            Perfect, congratulations! 🎉
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchWordsAdults;
